import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, reduxForm, formValueSelector, change } from 'redux-form';
import { formSettings } from './settings';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import messages from '../../../../../text/messages';
import { required, minLength8, validABA, errorMessages } from './../../../../../utilities/validation';
import { SelectField, CheckboxField } from './../../../../../components/fields';
import Modal from './../../../../../components/modal';
import WizardButtons from '../../../../../components/wizard/navigator/buttons';
import InviteForm from './../../../../../components/forms/invite';
import { getBanks, updateBankRecord, createBankRecord, deleteBankRecord } from '../../../../../reducers/banking';
import { getUsers, usersAreLoaded } from '../../../../../reducers/users';
import { sendInvite } from '../../../../../reducers/invites';
import { RoleDefinitions } from '../../../../../utilities/roles';
import { updateUserDetails } from '../../../../../reducers/identity';
import { getAdmin } from '../../../../../reducers/onboarding';
import Bank from '../../../../../components/forms/bank';

class BillingStep extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            modal: { open: false }
        };

        this.submit = this.submit.bind(this);

        // modal controls
        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    componentDidMount() {
        let { role, admin, banks, client, users, usersRole, getBanks, getUsers, getAdmin } = this.props;
        if (!banks) getBanks(role);
        if (!users || !usersAreLoaded(role, usersRole)) getUsers(role);
        if (!admin) getAdmin(client.admin_id);
    }

    onOpen() {
        this.setState({ modal: { open: true } });
    }

    onClose() {
        this.setState({ modal: { open: false } });
    }

    submit(values, action) {
        const { role, client, createBankRecord, updateBankRecord, deleteBankRecord, getBanks, updateUserDetails, banks } = this.props;
        const { bank, bankingCheckPreference } = values;

        updateUserDetails(role, { ...client, ap_user: values.ap_user }, () => {
            if (bankingCheckPreference) {
                if (bank && bank.id) {
                    deleteBankRecord(bank.id, () => getBanks(role, action));
                } else if (banks.length) {
                    deleteBankRecord(banks[0].id, () => getBanks(role, action));
                } else {
                    action();
                }
                return;
            }
            if (!bank || (bank && bank.name === 'n/a')) {
                action();
                return;
            }

            if (bank.id === undefined) {
                createBankRecord(bank, role, () => getBanks(role, action));
            } else {
                updateBankRecord(bank, role, () => getBanks(role, action));
            }
        });
    }

    render() {
        const { modal } = this.state;
        const {
            handleSubmit,
            onSubmit,
            back,
            submitting,
            role,
            banks,
            admin,
            client,
            changeField,
            sendInvite,
            getUsers,
            users,
            bankingCheckPreference
        } = this.props;
        if (!admin || !banks || !users) {
            return null;
        }

        return (
            <div>
                <form onSubmit={handleSubmit(values => this.submit(values, onSubmit))}>
                    <div className="row section">
                        <div className="col-xs-12">
                            <FormattedHTMLMessage
                                id={'clientOnboarding.billingIntroText'}
                                defaultMessage={
                                    "<p>To pay your workers promptly, {admin} will debit the approved amount from {client}'s bank account automatically ahead of each payroll, just as other payroll services do.</p><p>Please enter your banking information below. {admin} will conduct an ACH check to confirm we can transact directly.</p>"
                                }
                                values={{ admin: admin.name, client: client.name }}
                                tagName={'div'}
                            />
                        </div>
                    </div>
                    <Bank
                        manuallyValidated={true}
                        fieldsOptions={{
                            bankName: { name: 'bank.name' },
                            bankRouting: { name: 'bank.routing', size: 'col-xs-12 col-sm-4' },
                            bankAccount: { name: 'bank.account', size: 'col-xs-12 col-sm-4' }
                        }}
                    />
                    <div className="row group">
                        <div className="col-xs-12 col-sm-8">
                            <Field
                                name="bankingCheckPreference"
                                component={CheckboxField}
                                checked={bankingCheckPreference === true}
                                onChange={e => {
                                    let value = e.target.value !== 'true' ? 'n/a' : null;
                                    changeField(formSettings.form, 'bank', { name: value, routing: value, account: value });
                                }}
                                placeholder={messages.addLater}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-xs-12 col-sm-8">
                            <div className="row">
                                <div className="col-xs-10">
                                    <Field
                                        name="ap_user"
                                        id="onboarding-ap-contact"
                                        component={SelectField}
                                        options={users.map(user => ({ value: user.id, label: `${user.first_name} ${user.last_name}` }))}
                                        placeholder={messages.apContact}
                                        validate={required}
                                    />
                                </div>
                                <div className="col-xs-2">
                                    <button type="button" className="field-inline-button" onClick={this.onOpen}>
                                        <FormattedMessage {...messages.add} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <WizardButtons id={{ id: 'onboarding-billing-continue' }} disabled={submitting} back={{ action: back }} />
                </form>
                <Modal
                    open={modal.open}
                    onClose={this.onClose}
                    managedClose
                    size="col-sm-8 col-sm-offset-2 col-xs-12"
                    title={<FormattedMessage {...messages.addUser} />}
                >
                    <div className="row">
                        <InviteForm
                            onSubmit={values =>
                                sendInvite({ ...values, clientId: client.id }, role, () =>
                                    getUsers(role, result => {
                                        let newUser = result.find(u => u.email === values.email);
                                        changeField(formSettings.form, 'ap_user', newUser.id);
                                        this.onClose();
                                    })
                                )
                            }
                            cancel={this.onClose}
                            tenantType={'client'}
                            initialValues={{ ref_id: role.id, ref_type: role.type, company_name: client.name }}
                            availableRoles={[RoleDefinitions.Client]}
                        />
                    </div>
                </Modal>
            </div>
        );
    }
}

BillingStep = reduxForm({
    ...formSettings,
    validate: values => {
        if (values.bankingCheckPreference === true || !values.bank) {
            return {};
        }
        const errors = { bank: {} };
        errors.bank.name = values.bank.name ? undefined : errorMessages.required();
        errors.bank.routing = values.bank.routing ? validABA(values.bank.routing) : errorMessages.required();
        errors.bank.account = values.bank.account ? minLength8(values.bank.account) : errorMessages.required();
        return errors;
    }
})(BillingStep);

const selector = formValueSelector(formSettings.form);
const mapStateToProps = state => {
    let banks = state.banking.banks;
    let client = state.identity.details;
    let initialValues = {
        bank: banks && banks.length > 0 ? banks[0] : undefined,
        ap_user: client.ap_user
    };
    return {
        bankingCheckPreference: selector(state, 'bankingCheckPreference') && selector(state, 'bankingCheckPreference') === true,
        role: state.identity.role,
        admin: state.onboarding.admin,
        client,
        banks,
        users: state.users.list,
        usersRole: state.users.listRole,
        initialValues
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            changeField: change,
            getAdmin: getAdmin,
            getBanks: getBanks,
            createBankRecord: createBankRecord,
            updateBankRecord: updateBankRecord,
            getUsers: getUsers,
            sendInvite: sendInvite,
            updateUserDetails: updateUserDetails,
            deleteBankRecord: deleteBankRecord
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(BillingStep);



// WEBPACK FOOTER //
// ./src/pages/onboarding/tenants/client/steps/billing.jsx